/**
 * Receipt printing and cash-drawer control.
 *
 * The register talks to a small print bridge running on the store computer
 * (print-bridge/bridge.js, default http://localhost:9100), because a browser
 * cannot write raw ESC/POS to a USB printer or pulse a cash drawer. Karen is
 * served over HTTPS; calls to http://localhost are exempt from mixed-content
 * blocking in Chrome, so this works from the hosted app.
 *
 * Everything here degrades instead of failing:
 *   - printReceipt falls back to a browser print window when the bridge is down,
 *     so a customer can always be handed something.
 *   - kickDrawer reports failure rather than throwing, so a card sale is never
 *     lost because a drawer did not open.
 *
 * Before 9/14 none of this was connected: the one PRINT RECEIPT button opened a
 * window containing a JSON dump of the sale row, and the drawer never opened.
 */
import api from "./api";

const DEFAULT_BRIDGE = "http://localhost:9100";
const PROBE_TIMEOUT_MS = 1200;
const PRINT_TIMEOUT_MS = 6000;
const HEALTH_TTL_MS = 30_000;

export function bridgeUrl(): string {
  // Create React App: build-time env vars must be REACT_APP_*. Overriding this is
  // only needed if the bridge is moved off the register machine or off port 9100.
  const configured = process.env.REACT_APP_PRINT_BRIDGE_URL;
  return (configured || DEFAULT_BRIDGE).replace(/\/$/, "");
}

let lastHealth: { at: number; ok: boolean; features?: string[] } | null = null;

async function withTimeout(url: string, init: RequestInit, ms: number): Promise<Response> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  try {
    return await fetch(url, { ...init, signal: ctrl.signal });
  } finally {
    clearTimeout(timer);
  }
}

/** Is the local print bridge up? Cached briefly so the register does not probe on every sale. */
export async function bridgeHealthy(force = false): Promise<boolean> {
  if (!force && lastHealth && Date.now() - lastHealth.at < HEALTH_TTL_MS) return lastHealth.ok;
  let ok = false;
  let features: string[] = [];
  try {
    const res = await withTimeout(`${bridgeUrl()}/health`, { method: "GET" }, PROBE_TIMEOUT_MS);
    ok = res.ok;
    if (ok) {
      try { features = (await res.json())?.features || []; } catch { features = []; }
    }
  } catch {
    ok = false;
  }
  lastHealth = { at: Date.now(), ok, features };
  return ok;
}

export type PrintOutcome = "printed" | "browser" | "failed";

/** Open a browser print window with the plain-text receipt. Always the last resort. */
function printInBrowser(text: string): boolean {
  const w = window.open("", "_blank", "width=380,height=640");
  if (!w) return false;
  const escaped = text.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c] as string));
  w.document.write(
    `<html><head><title>Receipt</title><style>
      @page { margin: 6mm; }
      body { font-family: "Courier New", monospace; font-size: 12px; line-height: 1.35;
             white-space: pre; padding: 8px; margin: 0; }
    </style></head><body>${escaped}</body></html>`
  );
  w.document.close();
  w.focus();
  w.print();
  return true;
}

/**
 * Print a receipt for a completed sale.
 * Tries the thermal printer first, then the browser. Never throws.
 */
export async function printReceipt(saleId: string): Promise<PrintOutcome> {
  if (await bridgeHealthy()) {
    try {
      // A bridge that decodes base64 gets the receipt with the logo image;
      // an older one gets plain text it can print as-is.
      const b64 = (lastHealth?.features || []).includes("b64");
      const raw = await api.get(`/hardware/receipt/${saleId}`, {
        responseType: "text",
        params: b64 ? { format: "b64", logo: "1" } : undefined,
      });
      const headers: Record<string, string> = { "Content-Type": "text/plain" };
      if (b64) headers["X-Body-Encoding"] = "base64";
      const res = await withTimeout(
        `${bridgeUrl()}/print/receipt`,
        { method: "POST", headers, body: String(raw.data) },
        PRINT_TIMEOUT_MS
      );
      if (res.ok) return "printed";
    } catch {
      lastHealth = null; // re-probe next time; the bridge may have just died
    }
  }
  try {
    const res = await api.get(`/hardware/receipt/${saleId}/json`);
    const text = res.data?.text;
    if (typeof text === "string" && text.trim()) return printInBrowser(text) ? "browser" : "failed";
  } catch {
    /* fall through */
  }
  return "failed";
}

/**
 * Pulse the cash drawer open. Returns false when there is no bridge to do it,
 * which the caller should surface quietly rather than treat as a failed sale.
 */
export async function kickDrawer(): Promise<boolean> {
  if (!(await bridgeHealthy())) return false;
  try {
    const res = await withTimeout(`${bridgeUrl()}/drawer/kick`, { method: "POST" }, PRINT_TIMEOUT_MS);
    return res.ok;
  } catch {
    lastHealth = null;
    return false;
  }
}
